/**
 * Übersetzung zwischen URL-Suchparametern und Listenzustand der Apartmentliste.
 *
 * <p>Gleiche Bauart wie `bookingsListSearch` (Block A) und `usersListSearch` (Block E) und aus
 * demselben Grund (§5): Reload, Zurück-Taste und ein geteilter Link stellen denselben
 * Arbeitskontext wieder her. Hier kommt ein zweiter Grund dazu — die Vor-/Zurück-Pfeile der
 * Detailansicht (F4) brauchen genau diesen Kontext, um zu wissen, welche Nachbarn die Liste
 * gerade zeigt. Ohne URL-Zustand wäre die Nachbarschaft nach einem Reload eine andere als die
 * Liste, aus der der Nutzer kam.</p>
 *
 * <p>Reine Funktionen, damit der Vertrag testbar bleibt und nicht an der Komponente hängt.</p>
 */
import type { PropertyListLifecycle, PropertyResponse } from '@/shared/api';

export interface PropertiesSearch {
  q?: string;
  bestand?: string;
  stadt?: string;
  seite?: number;
}

export interface PropertyFilterState {
  query: string;
  lifecycle: PropertyListLifecycle;
  city: string | 'all';
}

const LIFECYCLES: PropertyListLifecycle[] = ['Active', 'Archived', 'All'];

/** Der Bestandsfilter ist vorbelegt: eine Liste beginnt mit dem, was in Betrieb ist. */
export const DEFAULT_PROPERTY_FILTERS: PropertyFilterState = {
  query: '',
  lifecycle: 'Active',
  city: 'all',
};

export function propertyFiltersFromSearch(search: PropertiesSearch): PropertyFilterState {
  return {
    query: typeof search.q === 'string' ? search.q : '',
    lifecycle: LIFECYCLES.find((l) => l === search.bestand) ?? 'Active',
    city: typeof search.stadt === 'string' && search.stadt.trim() !== '' ? search.stadt : 'all',
  };
}

export function propertyPageFromSearch(search: PropertiesSearch): number {
  const page = Number(search.seite);
  return Number.isFinite(page) && page > 1 ? Math.floor(page) : 1;
}

/** Vorbelegungen werden weggelassen, damit eine ungefilterte Liste eine saubere URL behält. */
export function propertiesSearchFromState(state: {
  filters: PropertyFilterState;
  page: number;
}): PropertiesSearch {
  return {
    q: state.filters.query.trim() || undefined,
    bestand: state.filters.lifecycle === 'Active' ? undefined : state.filters.lifecycle,
    stadt: state.filters.city === 'all' ? undefined : state.filters.city,
    seite: state.page > 1 ? state.page : undefined,
  };
}

export function isPropertiesFiltered(s: PropertyFilterState): boolean {
  return s.query.trim() !== '' || s.lifecycle !== 'Active' || s.city !== 'all';
}

/**
 * Was die Liste von einer Serverseite tatsächlich zeigt.
 *
 * <p>Der Bestandsfilter läuft zusätzlich hier, weil die Seite ihn auch an den Endpunkt gibt —
 * doppelt gehalten ist er die Absicherung gegen eine Antwort, die ihn ignoriert. Der Stadtfilter
 * dagegen wirkt <em>nur</em> hier: der Endpunkt kennt keinen Stadtparameter. Die Liste sagt das
 * ausdrücklich, statt eine Auswahl über den Gesamtbestand zu behaupten, die sie nicht trifft.</p>
 */
export function applyLocalFilters(items: PropertyResponse[], filters: PropertyFilterState) {
  return items.filter((p) => {
    if (filters.lifecycle === 'Active' && p.status === 'Archived') return false;
    if (filters.lifecycle === 'Archived' && p.status !== 'Archived') return false;
    if (filters.city !== 'all' && p.address.city !== filters.city) return false;
    return true;
  });
}
